import { Button, Card, CardBody, CardHeader, Chip, Divider, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@nextui-org/react";
import { ArrowPathIcon, EyeIcon, ArrowDownTrayIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import { BASE_URL, getFetcher } from "../../../../services/api";
import useSWR from "swr";
import { useMemo, useState, useEffect, useCallback } from "react";
import { formattedDate, calculateRegistrasiRekomendasiProcessingDays } from "../../../../services/helpers";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import { endOfYear, format, startOfYear } from "date-fns";
import Cookies from "js-cookie";

export default function IndexPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [isExporting, setIsExporting] = useState(false);

  // Set default tanggal awal tahun dan akhir tahun
  useEffect(() => {
    const currentYearStart = format(startOfYear(new Date()), "yyyy-MM-dd");
    const currentYearEnd = format(endOfYear(new Date()), "yyyy-MM-dd");
    setStartDate(currentYearStart);
    setEndDate(currentYearEnd);
  }, []);

  // API URL untuk data recap
  const apiUrl = useMemo(() => {
    const actualPage = currentPage + 1;
    let baseParams = `returnAll=false&startDate=${startDate}&endDate=${endDate}&page=${actualPage}&limit=${currentPageSize}&sortBy=tanggal_pengajuan&sortOrder=desc`;

    if (searchQuery.trim()) {
      baseParams += `&search=${encodeURIComponent(searchQuery.trim())}`;
    }

    return `/api/registrasi/find-all/search?${baseParams}`;
  }, [startDate, endDate, searchQuery, currentPage, currentPageSize]);

  const {
    data: recapData,
    isLoading: loadingRecap,
    mutate: mutateRecap,
  } = useSWR(apiUrl, getFetcher, {
    keepPreviousData: true,
  });

  const columns = useMemo(
    () => [
      { field: "index", headerName: "No", width: 80 },
      { field: "nomor", headerName: "Nomor Registrasi", width: 150 },
      {
        field: "nama_kustomer",
        headerName: "Nama Customer",
        width: 200,
        valueGetter: (value, row) => row?.company?.name,
      },
      {
        field: "nama_sub_layanan",
        headerName: "Sub Layanan",
        width: 200,
        valueGetter: (value, row) => {
          let subLayanan = row?.sub_layanan;
          if (subLayanan === "119") {
            return "Registrasi B3 (Produsen)";
          } else {
            let kategoriBahan = row?.BahanB3Registrasi?.[0]?.kategori_b3;
            return `Registrasi B3 ${kategoriBahan === "Dapat Dipergunakan" ? "(Baru)" : kategoriBahan === "Terbatas Dipergunakan" ? "(Terbatas)" : ""}`;
          }
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="faded" className="capitalize">
            {params.value}
          </Chip>
        ),
      },
      { field: "tahun", headerName: "Tahun", width: 80, valueGetter: (value, row) => row?.tahun },
      {
        field: "bulan",
        headerName: "Bulan",
        width: 80,
        valueGetter: (value, row) => row?.bulan,
      },
      { field: "nomor_surat", headerName: "Nomor SK", width: 150, valueGetter: (value, row) => (row?.nomor_surat ? "S." + row?.nomor_surat : "-" || "-") },
      { field: "nomor_lampiran", headerName: "Nomor Lampiran", width: 150, valueGetter: (value, row) => row?.nomor_surat_formatted || "-" },
      {
        field: "tgl_selesai",
        headerName: "Tanggal Selesai",
        width: 130,
        valueGetter: (value, row) => row?.tanggal_selesai,
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "tanggal_proses",
        headerName: "Tanggal Proses",
        width: 130,
        valueGetter: (value, row) => row?.tanggal_pengajuan,
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "lama_proses",
        headerName: "Lama Proses (Hari)",
        width: 80,
        valueGetter: (value, row) => calculateProcessingDays(row?.tanggal_selesai, row?.tanggal_pengajuan),
        renderCell: (params) => calculateProcessingDays(params.row?.tanggal_selesai, params.row?.tanggal_pengajuan).toString(),
      },
      {
        field: "SLA",
        headerName: "SLA",
        width: 80,
        valueGetter: (value, row) => calculateProcessingDays(row?.tanggal_selesai, row?.tanggal_pengajuan),
        renderCell: (params) => calculateProcessingDays(params.row?.tanggal_selesai, params.row?.tanggal_pengajuan).toString(),
      },
      {
        field: "tgl_terbit",
        headerName: "Tanggal Terbit",
        width: 130,
        valueGetter: (value, row) => row?.tanggal_terbit,
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "alamat",
        headerName: "Alamat",
        width: 200,
        valueGetter: (value, row) => row?.company?.alamatKantor,
      },
      {
        field: "kode_provinsi",
        headerName: "Kode Provinsi",
        width: 120,
        valueGetter: (value, row) => row?.company?.provinceId,
      },
      {
        field: "provinsi",
        headerName: "Provinsi",
        width: 150,
        valueGetter: (value, row) => row?.company?.provinsi,
      },
      {
        field: "kode_kabkot",
        headerName: "Kode Kab/Kota",
        width: 120,
        valueGetter: (value, row) => row?.company?.kota,
      },
      {
        field: "cas_number",
        headerName: "CAS Number",
        width: 120,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.cas_number,
      },
      {
        field: "nama_bahan_kimia",
        headerName: "Nama Bahan Kimia",
        width: 200,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.nama_bahan,
      },
      {
        field: "nama_dagang",
        headerName: "Nama Dagang",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.nama_dagang,
      },
      {
        field: "id_kategori",
        headerName: "ID Kategori",
        width: 120,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.id_kategori || "-",
      },
      {
        field: "nama_kategori",
        headerName: "Nama Kategori",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.kategori_b3 || "-",
      },
      {
        field: "id_klasifikasi_b3",
        headerName: "ID Klasifikasi B3",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.id_klasifikasi?.join(", ") || "-",
      },
      {
        field: "nama_klasifikasi_b3",
        headerName: "Nama Klasifikasi B3",
        width: 200,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.klasifikasi_b3?.join(", ") || "-",
      },
      {
        field: "id_karakteristik_b3",
        headerName: "ID Karakteristik B3",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.id_karakteristik?.join(", ") || "-",
      },
      {
        field: "nama_karakteristik_b3",
        headerName: "Nama Karakteristik B3",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.karakteristik_b3?.join(", ") || "-",
      },
      {
        field: "tujuan_penggunaan",
        headerName: "Tujuan Penggunaan",
        width: 200,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.tujuan_penggunaan,
      },

      {
        field: "asal_negara",
        headerName: "Asal Negara",
        width: 120,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.asal_negara?.join(", "),
      },
      {
        field: "nama_asal_negara",
        headerName: "Nama Asal Negara",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.nama_asal_negara,
      },
      {
        field: "negara_muat",
        headerName: "Negara Muat",
        width: 120,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.negara_muat?.join(", "),
      },
      {
        field: "alamat_penghasil",
        headerName: "Alamat Penghasil",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.alamat_penghasil_b3,
      },
      {
        field: "pelabuhan_asal",
        headerName: "Pelabuhan Asal",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.pelabuhan_asal?.join(", "),
      },
      {
        field: "nama_pelabuhan_asal",
        headerName: "Nama Pelabuhan Asal",
        width: 200,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.nama_pelabuhan_asal,
      },
      {
        field: "pelabuhan_bongkar",
        headerName: "Pelabuhan Bongkar",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.pelabuhan_bongkar?.join(", "),
      },
      {
        field: "nama_pelabuhan_bongkar",
        headerName: "Nama Pelabuhan Bongkar",
        width: 200,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.nama_pelabuhan_bongkar,
      },
      {
        field: "provinsi_tujuan",
        headerName: "Provinsi Tujuan",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.provinsi_tujuan?.join(", "),
      },
      {
        field: "jml_di_impor",
        headerName: "Jumlah Import",
        width: 120,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.jumlah_impor,
      },
      {
        field: "jml_impor_per_tahun",
        headerName: "Jumlah Import/Tahun",
        width: 150,
        valueGetter: (value, row) => row?.BahanB3Registrasi?.[0]?.jumlah_impor_per_tahun,
      },
    ],
    []
  );

  const handleExport = useCallback(
    async (format) => {
      try {
        setIsExporting(true);

        const exportUrl = `/api/registrasi/export?format=${format}&startDate=${startDate}&endDate=${endDate}`;

        // Gunakan getFetcher untuk mendapatkan data dengan token
        const response = await fetch(`${BASE_URL}${exportUrl}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("accessToken")}`, // Ubah dari localStorage ke Cookies
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Export failed");
        }

        // Mendapatkan blob dari response
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Set nama file berdasarkan format dan tanggal
        const fileName = `rekap_registrasi_${startDate}_${endDate}.${format === "csv" ? "csv" : "xlsx"}`;

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting data:", error);
        // Bisa tambahkan toast error disini
      } finally {
        setIsExporting(false);
      }
    },
    [startDate, endDate]
  );

  // Alternative function jika API mengembalikan blob
  const handleExportBlob = useCallback(
    async (format) => {
      try {
        setIsExporting(true);

        const exportUrl = `${BASE_URL}/api/registrasi/export?format=${format}&startDate=${startDate}&endDate=${endDate}`;

        const response = await fetch(exportUrl);

        if (!response.ok) {
          throw new Error("Export failed");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `rekap_registrasi_${startDate}_${endDate}.${format === "csv" ? "csv" : "xlsx"}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting data:", error);
        // Bisa tambahkan toast error disini
      } finally {
        setIsExporting(false);
      }
    },
    [startDate, endDate]
  );

  const calculateProcessingDays = (tanggalSelesai, tanggalPengajuan) => {
    if (!tanggalSelesai || !tanggalPengajuan) {
      return 0;
    }

    try {
      const dateSelesai = new Date(tanggalSelesai);
      const datePengajuan = new Date(tanggalPengajuan);

      if (isNaN(dateSelesai.getTime()) || isNaN(datePengajuan.getTime())) {
        return 0;
      }

      const diffTime = dateSelesai - datePengajuan;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return Math.max(0, diffDays);
    } catch (error) {
      return 0;
    }
  };

  // ...existing columns code...

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleStartDateChange = useCallback((event) => {
    setStartDate(event.target.value);
    setCurrentPage(0);
  }, []);

  const handleEndDateChange = useCallback((event) => {
    setEndDate(event.target.value);
    setCurrentPage(0);
  }, []);

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader className="flex justify-between items-center">
          <span>Rekapitulasi Pendaftaran</span>
        </CardHeader>
        <Divider />
        <CardBody className="w-full">
          {/* Filter Section */}
          <div className="mb-4 space-y-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex flex-col gap-1">
                <label htmlFor="start-date" className="text-sm font-medium">
                  Tanggal Mulai
                </label>
                <input type="date" id="start-date" value={startDate} onChange={handleStartDateChange} className="px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="end-date" className="text-sm font-medium">
                  Tanggal Akhir
                </label>
                <input type="date" id="end-date" value={endDate} onChange={handleEndDateChange} className="px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>

              {/* Export Button */}
              <Dropdown>
                <DropdownTrigger>
                  <Button size="sm" color="primary" isLoading={isExporting} isDisabled={!startDate || !endDate} startContent={<ArrowDownTrayIcon className="size-4" />} endContent={<ChevronDownIcon className="size-3" />}>
                    Export
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Export options" onAction={(key) => handleExport(key)}>
                  <DropdownItem key="csv" className="text-primary">
                    Export CSV
                  </DropdownItem>
                  <DropdownItem key="excel" className="text-primary">
                    Export Excel
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          {/* Data Grid */}
          <div className="w-full h-[600px]">
            <ClientDataGrid
              columns={columns}
              rows={recapData?.registrasi || []}
              isLoading={loadingRecap}
              rowCount={recapData?.total || 0}
              pagination
              page={currentPage}
              pageSize={currentPageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              paginationMode="server"
            />
          </div>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}
